import React, { useEffect, useState } from 'react';
import { SaveIcon, XCircleIcon, AlertTriangleIcon, CheckCircleIcon, InfoIcon, BellIcon, SettingsIcon, EyeIcon, XIcon } from 'lucide-react';
import { Message, MessageType, MessageModule, Role } from './MessagesManagement';
import { MessagePreview } from './MessagePreview';
interface MessageFormProps {
  message: Message | null;
  availableRoles: Role[];
  currentUserRoles: string[];
  onSave: (message: Message) => void;
  onCancel: () => void;
  locale: string;
}
export function MessageForm({
  message,
  availableRoles,
  currentUserRoles,
  onSave,
  onCancel,
  locale
}: MessageFormProps) {
  const isAdmin = currentUserRoles.includes('Administrator');
  const [formData, setFormData] = useState<Omit<Message, 'id' | 'createdAt' | 'createdBy' | 'usageCount'>>({
    key: '',
    defaultMessage: '',
    overriddenMessage: undefined,
    type: 'Info',
    module: 'Settings',
    context: '',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: [],
    modifiedAt: undefined,
    modifiedBy: undefined,
    lastUsedAt: undefined
  });
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState<'banner' | 'toast' | 'inline'>('banner');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  // Initialize form data when editing an existing message
  useEffect(() => {
    if (message) {
      setFormData({
        key: message.key,
        defaultMessage: message.defaultMessage,
        overriddenMessage: message.overriddenMessage,
        type: message.type,
        module: message.module,
        context: message.context || '',
        languageCode: message.languageCode,
        isActive: message.isActive,
        isSystemLocked: message.isSystemLocked,
        editableByRoles: message.editableByRoles,
        modifiedAt: message.modifiedAt,
        modifiedBy: message.modifiedBy,
        lastUsedAt: message.lastUsedAt
      });
      setSelectedRoles(message.editableByRoles);
    }
  }, [message]);
  // Determine if current user can edit this message type
  const canEditMessageType = (type: MessageType): boolean => {
    if (isAdmin) return true;
    const userRole = availableRoles.find(role => currentUserRoles.includes(role.name));
    if (!userRole) return false;
    switch (type) {
      case 'System':
        return userRole.canEditSystemMessages;
      case 'Error':
        return userRole.canEditErrorMessages;
      case 'Warning':
        return userRole.canEditWarningMessages;
      case 'Success':
        return userRole.canEditSuccessMessages;
      case 'Info':
        return userRole.canEditInfoMessages;
      case 'Notification':
        return userRole.canEditNotificationMessages;
      default:
        return false;
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    // Special handling for message type to enforce RBAC
    if (name === 'type' && !canEditMessageType(value as MessageType)) {
      setErrors({
        ...errors,
        type: `You don't have permission to create or edit ${value} messages`
      });
      return;
    }
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field if it exists
    if (errors[name]) {
      const newErrors = {
        ...errors
      };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  const handleRoleToggle = (role: string) => {
    const newSelectedRoles = selectedRoles.includes(role) ? selectedRoles.filter(r => r !== role) : [...selectedRoles, role];
    setSelectedRoles(newSelectedRoles);
    setFormData({
      ...formData,
      editableByRoles: newSelectedRoles
    });
  };
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.key) {
      newErrors.key = 'Key is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.key)) {
      newErrors.key = 'Key must contain only uppercase letters, numbers, and underscores';
    }
    if (!formData.defaultMessage) {
      newErrors.defaultMessage = 'Default message is required';
    }
    if (!canEditMessageType(formData.type)) {
      newErrors.type = `You don't have permission to create or edit ${formData.type} messages`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        ...(message ? {
          id: message.id
        } : {
          id: ''
        }),
        ...formData,
        createdAt: message?.createdAt || new Date().toISOString(),
        createdBy: message?.createdBy || 'Current User',
        usageCount: message?.usageCount || 0
      });
    }
  };
  const getTypeIcon = (type: MessageType) => {
    switch (type) {
      case 'Error':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-16353" />;
      case 'Warning':
        return <AlertTriangleIcon className="h-5 w-5 text-amber-500" data-id="element-16354" />;
      case 'Success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-16355" />;
      case 'Info':
        return <InfoIcon className="h-5 w-5 text-blue-500" data-id="element-16356" />;
      case 'Notification':
        return <BellIcon className="h-5 w-5 text-purple-500" data-id="element-16357" />;
      case 'System':
        return <SettingsIcon className="h-5 w-5 text-gray-500" data-id="element-16358" />;
      default:
        return null;
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-16359">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-16360">
        <div data-id="element-16361">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16362">
            {message ? 'Edit Message' : 'Add New Message'}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16363">
            {message ? 'Update an existing system message or notification' : 'Create a new system message or notification'}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-16364">
          <button type="button" onClick={() => setShowPreview(!showPreview)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16365">
            <EyeIcon className="h-4 w-4 mr-2" data-id="element-16366" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
      </div>
      <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-16367">
        {showPreview && <div className="mb-6" data-id="element-16368">
            <div className="flex justify-between items-center mb-3" data-id="element-16369">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-16370">Preview</h4>
              <div className="flex items-center space-x-4" data-id="element-16371">
                <div className="flex items-center" data-id="element-16372">
                  <input type="radio" id="preview-banner" name="preview-type" value="banner" checked={previewType === 'banner'} onChange={() => setPreviewType('banner')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-16373" />
                  <label htmlFor="preview-banner" className="ml-2 text-sm text-gray-700" data-id="element-16374">
                    Banner
                  </label>
                </div>
                <div className="flex items-center" data-id="element-16375">
                  <input type="radio" id="preview-toast" name="preview-type" value="toast" checked={previewType === 'toast'} onChange={() => setPreviewType('toast')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-16376" />
                  <label htmlFor="preview-toast" className="ml-2 text-sm text-gray-700" data-id="element-16377">
                    Toast
                  </label>
                </div>
                <div className="flex items-center" data-id="element-16378">
                  <input type="radio" id="preview-inline" name="preview-type" value="inline" checked={previewType === 'inline'} onChange={() => setPreviewType('inline')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-16379" />
                  <label htmlFor="preview-inline" className="ml-2 text-sm text-gray-700" data-id="element-16380">
                    Inline
                  </label>
                </div>
              </div>
            </div>
            <MessagePreview message={{
          type: formData.type,
          text: formData.overriddenMessage || formData.defaultMessage
        }} displayType={previewType} data-id="element-16381" />
          </div>}
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-16382">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-16383">
            {/* Message Type */}
            <div className="sm:col-span-3" data-id="element-16384">
              <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-16385">
                Message Type <span className="text-red-500" data-id="element-16386">*</span>
              </label>
              <div className="mt-1" data-id="element-16387">
                <select id="type" name="type" value={formData.type} onChange={handleChange} className={`shadow-sm block w-full sm:text-sm rounded-md ${errors.type ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`} data-id="element-16388">
                  {(['Error', 'Warning', 'Success', 'Info', 'Notification', 'System'] as MessageType[]).map(type => <option key={type} value={type} disabled={!canEditMessageType(type)} data-id="element-16389">
                      {type}
                    </option>)}
                </select>
                {errors.type && <p className="mt-2 text-sm text-red-600" data-id="element-16390">{errors.type}</p>}
                <p className="mt-2 text-xs text-gray-500 flex items-center" data-id="element-16391">
                  {getTypeIcon(formData.type)}
                  <span className="ml-1" data-id="element-16392">
                    {formData.type === 'Error' && 'System or business logic errors (400, 500, etc.)'}
                    {formData.type === 'Warning' && 'Non-blocking alerts for attention'}
                    {formData.type === 'Success' && 'Success confirmation messages'}
                    {formData.type === 'Info' && 'Informational messages, tips, helper text'}
                    {formData.type === 'Notification' && 'In-app or email notifications'}
                    {formData.type === 'System' && 'Core system messages (e.g., auth, tenant bootstraps)'}
                  </span>
                </p>
              </div>
            </div>
            {/* Module */}
            <div className="sm:col-span-3" data-id="element-16393">
              <label htmlFor="module" className="block text-sm font-medium text-gray-700" data-id="element-16394">
                Module <span className="text-red-500" data-id="element-16395">*</span>
              </label>
              <div className="mt-1" data-id="element-16396">
                <select id="module" name="module" value={formData.module} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-16397">
                  {(['AP', 'PO', 'VM', 'OCR', 'DI', 'Financial', 'Settings', 'Core'] as MessageModule[]).map(module => <option key={module} value={module} data-id="element-16398">
                      {module}
                    </option>)}
                </select>
              </div>
            </div>
            {/* Message Key */}
            <div className="sm:col-span-4" data-id="element-16399">
              <label htmlFor="key" className="block text-sm font-medium text-gray-700" data-id="element-16400">
                Message Key <span className="text-red-500" data-id="element-16401">*</span>
              </label>
              <div className="mt-1" data-id="element-16402">
                <input type="text" name="key" id="key" value={formData.key} onChange={handleChange} disabled={message?.isSystemLocked} placeholder="e.g., AP_ERR_DUPLICATEINVOICE" className={`shadow-sm block w-full sm:text-sm rounded-md ${errors.key ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`} data-id="element-16403" />
                {errors.key ? <p className="mt-2 text-sm text-red-600" data-id="element-16404">{errors.key}</p> : <p className="mt-2 text-xs text-gray-500" data-id="element-16405">
                    Use uppercase letters, numbers, and underscores. Prefix with
                    module name.
                  </p>}
              </div>
            </div>
            {/* Context */}
            <div className="sm:col-span-2" data-id="element-16406">
              <label htmlFor="context" className="block text-sm font-medium text-gray-700" data-id="element-16407">
                Context
              </label>
              <div className="mt-1" data-id="element-16408">
                <input type="text" name="context" id="context" value={formData.context} onChange={handleChange} placeholder="e.g., InvoiceEntry" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-16409" />
                <p className="mt-2 text-xs text-gray-500" data-id="element-16410">
                  Optional component or screen reference
                </p>
              </div>
            </div>
            {/* Default Message */}
            <div className="sm:col-span-6" data-id="element-16411">
              <label htmlFor="defaultMessage" className="block text-sm font-medium text-gray-700" data-id="element-16412">
                Default Message <span className="text-red-500" data-id="element-16413">*</span>
              </label>
              <div className="mt-1" data-id="element-16414">
                <textarea id="defaultMessage" name="defaultMessage" rows={3} value={formData.defaultMessage} onChange={handleChange} disabled={message?.isSystemLocked} className={`shadow-sm block w-full sm:text-sm rounded-md ${errors.defaultMessage ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`} data-id="element-16415" />
                {errors.defaultMessage && <p className="mt-2 text-sm text-red-600" data-id="element-16416">
                    {errors.defaultMessage}
                  </p>}
              </div>
            </div>
            {/* Overridden Message */}
            <div className="sm:col-span-6" data-id="element-16417">
              <label htmlFor="overriddenMessage" className="block text-sm font-medium text-gray-700" data-id="element-16418">
                Override Message
              </label>
              <div className="mt-1" data-id="element-16419">
                <textarea id="overriddenMessage" name="overriddenMessage" rows={3} value={formData.overriddenMessage || ''} onChange={handleChange} disabled={message?.isSystemLocked} placeholder="Enter custom override for this tenant (optional)" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-16420" />
                <p className="mt-2 text-xs text-gray-500" data-id="element-16421">
                  Optional tenant-specific override of the default message
                </p>
              </div>
            </div>
            {/* Status & System Lock */}
            <div className="sm:col-span-6" data-id="element-16422">
              <div className="flex items-start" data-id="element-16423">
                <div className="flex items-center h-5" data-id="element-16424">
                  <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleCheckboxChange} disabled={message?.isSystemLocked} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-16425" />
                </div>
                <div className="ml-3 text-sm" data-id="element-16426">
                  <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-16427">
                    Active
                  </label>
                  <p className="text-gray-500" data-id="element-16428">
                    Message will be displayed to users when active
                  </p>
                </div>
              </div>
              {isAdmin && !message?.isSystemLocked && <div className="flex items-start mt-4" data-id="element-16429">
                  <div className="flex items-center h-5" data-id="element-16430">
                    <input id="isSystemLocked" name="isSystemLocked" type="checkbox" checked={formData.isSystemLocked} onChange={handleCheckboxChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-16431" />
                  </div>
                  <div className="ml-3 text-sm" data-id="element-16432">
                    <label htmlFor="isSystemLocked" className="font-medium text-gray-700" data-id="element-16433">
                      System Locked
                    </label>
                    <p className="text-gray-500" data-id="element-16434">
                      Prevent this message from being edited or overridden
                    </p>
                  </div>
                </div>}
            </div>
            {/* Role-Based Access Control */}
            <div className="sm:col-span-6" data-id="element-16435">
              <fieldset data-id="element-16436">
                <legend className="text-sm font-medium text-gray-700" data-id="element-16437">
                  Editable By Roles
                </legend>
                <div className="mt-2 space-y-4" data-id="element-16438">
                  <p className="text-sm text-gray-500" data-id="element-16439">
                    Select which roles can edit this message. If none are
                    selected, only administrators can edit.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-16440">
                    {availableRoles.map(role => <div key={role.id} className="flex items-start" data-id="element-16441">
                        <div className="flex items-center h-5" data-id="element-16442">
                          <input id={`role-${role.id}`} type="checkbox" checked={selectedRoles.includes(role.name)} onChange={() => handleRoleToggle(role.name)} disabled={message?.isSystemLocked || !canEditMessageType(formData.type)} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-16443" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-16444">
                          <label htmlFor={`role-${role.id}`} className="font-medium text-gray-700" data-id="element-16445">
                            {role.name}
                          </label>
                          <p className="text-gray-500" data-id="element-16446">{role.description}</p>
                        </div>
                      </div>)}
                  </div>
                  {formData.type === 'System' && <div className="rounded-md bg-yellow-50 p-4" data-id="element-16447">
                      <div className="flex" data-id="element-16448">
                        <div className="flex-shrink-0" data-id="element-16449">
                          <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-16450" />
                        </div>
                        <div className="ml-3" data-id="element-16451">
                          <h3 className="text-sm font-medium text-yellow-800" data-id="element-16452">
                            Restricted Access
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700" data-id="element-16453">
                            <p data-id="element-16454">
                              System messages are restricted and can only be
                              edited by administrators and system maintainers.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>
              </fieldset>
            </div>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-16455">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16456">
              <XIcon className="h-4 w-4 mr-2" data-id="element-16457" />
              Cancel
            </button>
            <button type="submit" disabled={message?.isSystemLocked} className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${message?.isSystemLocked ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'}`} data-id="element-16458">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-16459" />
              {message ? 'Update Message' : 'Create Message'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}